import { useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import BillingDetails from './components/BillingDetails';
import IntegrationUsage from './components/IntegrationUsage';
import BillingHistory from './components/BillingHistory';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import Button from '@/components/Common/Button';
import {
  usePayAgencyDues,
  useGetAgencyCancellationPreview,
  useCancelAgencySubscription
} from '@/apis/agency/billing/hook';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import CancelSubscription, { buildCancelSubscriptionBillingPreview } from '@/components/Common/CancelSubscription';

export default function Billing() {
  const { data: OrganizationData } = useGetAgencyOrganizations();
  const { mutate: payAgencyDues, isPending: payingAgencyDues } = usePayAgencyDues();
  const { data: cancellationPreviewData } = useGetAgencyCancellationPreview();
  const cancelAgencySubscriptionMutation = useCancelAgencySubscription();
  const cancelBillingPreview = useMemo(
    () => buildCancelSubscriptionBillingPreview(cancellationPreviewData),
    [cancellationPreviewData]
  );
  const isPastDue = OrganizationData?.account_status === 'past_due';
  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Typography
        variant='h6'
        display='flex'
        alignItems='center'
        gap={1}
        fontWeight={500}
        sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}
      >
        Agency Billing & Usage
      </Typography>
      <Typography variant='body2' color='text.secondary' mb={2} sx={{ fontSize: { xs: '14px', lg: 16 } }} mt={0.5}>
        Manage your agency subscription and track usage across all businesses
      </Typography>

      {isPastDue && (
        <Box
          bgcolor='#FFFBEB'
          my={3}
          padding={2}
          border='1px solid #F59E0B'
          borderRadius='10px'
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
            <IoIosInformationCircleOutline color='#D97706' size={20} />
            <Box display='flex' flexDirection='column' gap={-2}>
              <Typography color='#D97706' fontSize={16} fontWeight={600} mb={-0.4}>
                Payment Pending
              </Typography>
              <Typography color='#D97706' fontSize={14}>
                Your previous invoice is overdue. Please complete payment to continue using all features.
              </Typography>
            </Box>
          </Box>
          <Button
            isPending={payingAgencyDues}
            sx={{ bgcolor: '#D97706', px: 5, minWidth: 40, fontWeight: 500, fontSize: '14px' }}
            onClick={() => payAgencyDues()}
          >
            Pay Now
          </Button>
        </Box>
      )}

      <Grid container spacing={{ xs: 2, lg: 3 }}>
        {/* Billing Details */}
        <Grid size={12}>
          <BillingDetails />
        </Grid>

        {/* Integration Usage */}
        <Grid size={12}>
          <Paper
            sx={{
              paddingY: { xs: 1.5, lg: 2 },
              paddingX: { xs: 1.5, lg: 0.5 },
              border: '1px solid #ECECEC',
              borderRadius: 2
            }}
            elevation={0}
          >
            <IntegrationUsage />
          </Paper>
        </Grid>

        {/* Billing History */}
        <Grid size={12}>
          <Paper
            sx={{
              p: { xs: 1.5, lg: 2 },
              border: '1px solid #ECECEC',
              borderRadius: 2
            }}
            elevation={0}
          >
            <BillingHistory />
          </Paper>
        </Grid>

        <Grid size={12}>
          <CancelSubscription
            agencyView
            billingPreview={cancelBillingPreview}
            cancelSubscription={cancelAgencySubscriptionMutation}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
